// Package
import React, { useState, useEffect } from "react";
import { useTheme, ClickAwayListener } from "@mui/material";
import {
  Box,
  Chip,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  styled,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";

import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidWarnButton } from "components/buttons/SolidWarnButton";

// Icon
import { ReactComponent as AddIcon } from "assets/images/icons/btn_topology_expand.svg";
import { ReactComponent as AddIconHover } from "assets/images/icons/btn_topology_expand_hover.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/icn_table_cell_Bin.svg";
import { ReactComponent as DeleteIconHover } from "assets/images/icons/icn_table_cell_Bin_hover.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_table_cell_Remove.svg";
import { ReactComponent as RemoveIconHover } from "assets/images/icons/icn_table_cell_Remove_hover.svg";
import { ReactComponent as PencilIcon } from "assets/images/icons/icn_table_cell_Pencil.svg";
import { ReactComponent as PencilIconHover } from "assets/images/icons/icn_table_cell_Pencil_hover.svg";
import { ReactComponent as ClearIcon } from "assets/images/icons/icn_table_cell_clear_solid.svg";
import { ReactComponent as ClearIconHover } from "assets/images/icons/icn_table_cell_clear_solid_hover.svg";
import { ReactComponent as QuestionIcon } from "assets/images/icons/icn_table_cell_Question_solid.svg";
import { ReactComponent as QuestionIconHover } from "assets/images/icons/icn_table_cell_Question_solid_hover.svg";
import { ReactComponent as AllowIcon } from "assets/images/icons/icn_table_cell_Allow_solid.svg";
import { ReactComponent as AllowIconHover } from "assets/images/icons/icn_table_cell_Allow_solid_hover.svg";

// Const
import { defaultHover, defaultAnchor, defaultTag } from "./const";

const StyledMainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1)
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.subBlue,
  borderRadius: "8px",
  fontWeight: theme.typography.customFontWeight[2],
  fontSize: theme.typography.customFontSize[0],
  "&:hover": { backgroundColor: theme.palette.primary.subBlue }
}));

const StyledRemoveButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.white,
  textTransform: "none",
  fontWeight: theme.typography.customFontWeight[2],
  fontSize: theme.typography.customFontSize[2],
  padding: "5px",
  "&:hover": {
    backgroundColor: theme.palette.primary.white,
    color: theme.palette.primary.mainHover,
  }
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "isEditing",
})(({ theme, isEditing }) => ({
  fontSize: theme.typography.customFontSize[2],
  padding: theme.spacing(0.6, 1),
  marginRight: theme.spacing(1),
  borderRadius: "6px",

  backgroundColor: isEditing ? "transparent" : "inherit",

  "&:hover": {
    backgroundColor: isEditing
      ? "transparent"
      : theme.palette.primary.lightBlue,
  },

  "&.Mui-focusVisible": {
    backgroundColor: isEditing ? "transparent" : undefined,
  },

  "&.Mui-selected": {
    backgroundColor: isEditing ? "transparent" : undefined,
  },
}));

const StyledTag = styled(ListItemText)(({ theme }) => ({
  "& .MuiListItemText-primary": {
    fontSize: theme.typography.customFontSize[3],
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.greyPrimary,
    },
  },
}));

const StyledScrollList = styled(List)(({ theme }) => ({
  maxHeight: 200,
  overflowY: "auto",
  padding: 0,
  "&::-webkit-scrollbar": { width: 5, height: 5 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.primary.mainHover,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.primary.greyThread,
    borderRadius: theme.shape.customBorderRadius[0],
  },
}));

const TagsIconButtonList = ({ buttons }) => (
  <>
    {buttons.map((btn, index) => (
      <IconButton
        key={index}
        edge="end"
        onClick={btn.onClick}
        onMouseEnter={btn.onMouseEnter}
        onMouseLeave={btn.onMouseLeave}
        disabled={btn.disabled}
      >
        {btn.label}
      </IconButton>
    ))}
  </>
);

const TagsList = ({
  theme,
  tag,
  isHover,
  setIsHover,
  handleEdit,
  editingTagValue,
  isChipsEdit,
  isEditing,
  setEditingTagId,
  tagOnClick = () => { },
  setEditingTagValue,
  handleModalOpen,
  primary
}) => (
  <ListItem
    disablePadding
    secondaryAction={
      <>
        {
          primary === "Root Node" ? (
            <Tooltip
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.primary.greyPrimaryTransparency,
                    color: theme.palette.primary.white,
                    borderRadius: theme.shape.customBorderRadius[1],
                    fontSize: theme.typography.customFontSize[1],
                  },
                },
                arrow: {
                  sx: {
                    color: theme.palette.primary.greyPrimaryTransparency,
                  },
                },
              }}
              title={
                <>
                  Marking a device with the<br />
                  '<strong>Root Node</strong>' tag will set it<br />
                  as the starting point (root)<br />
                  of the tree in Topology view.
                </>
              }
            >
              <IconButton
                edge="end"
                onMouseEnter={() => setIsHover(prev => ({ ...prev, question: true }))}
                onMouseLeave={() => setIsHover(prev => ({ ...prev, question: false }))}
              >
                {isHover?.question ? <QuestionIconHover /> : <QuestionIcon />}
              </IconButton>
            </Tooltip>
          ) :
            isEditing ? (
              <TagsIconButtonList
                buttons={[
                  {
                    onClick: (e) => handleEdit(e, "tag"),
                    onMouseEnter: () => setIsHover(prev => ({ ...prev, inputEdit: true })),
                    onMouseLeave: () => setIsHover(prev => ({ ...prev, inputEdit: false })),
                    disabled: editingTagValue === "",
                    label: isHover?.inputEdit ? <AllowIconHover /> : <AllowIcon />
                  },
                  {
                    onClick: () => setEditingTagId(null),
                    onMouseEnter: () => setIsHover(prev => ({ ...prev, cancel: true })),
                    onMouseLeave: () => setIsHover(prev => ({ ...prev, cancel: false })),
                    disabled: false,
                    label: isHover?.cancel ? <ClearIconHover /> : <ClearIcon />
                  }
                ]}
              />
            ) : (
              <TagsIconButtonList
                buttons={!isChipsEdit ? [] : [
                  {
                    onClick: (e) => {
                      e.stopPropagation();
                      setEditingTagId(tag.tagId);
                      setEditingTagValue(tag.tagName);
                    },
                    onMouseEnter: () => setIsHover(prev => ({ ...prev, edit: tag.tagId })),
                    onMouseLeave: () => setIsHover(prev => ({ ...prev, edit: tag.tagId })),
                    disabled: false,
                    label: isHover?.edit === tag.tagId ? <PencilIconHover /> : <PencilIcon />
                  },
                  {
                    onClick: (e) => handleModalOpen(e, { tagId: tag.tagId, tagName: tag.tagName }),
                    onMouseEnter: () => setIsHover(prev => ({ ...prev, delete: tag.tagId })),
                    onMouseLeave: () => setIsHover(prev => ({ ...prev, delete: tag.tagId })),
                    disabled: false,
                    label: isHover?.delete === tag.tagId ? <DeleteIconHover /> : <DeleteIcon />
                  }
                ]}
              />
            )}
      </>
    }
  >
    <StyledListItemButton isEditing={isEditing} onClick={() => tagOnClick(tag)}>
      {isEditing ? (
        <TextField
          fullWidth
          size="small"
          variant="standard"
          sx={{
            "& .MuiInputBase-input": {
              color: theme.palette.primary.greyPrimary,
            },
          }}
          value={editingTagValue}
          autoFocus
          onChange={(e) => setEditingTagValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <StyledTag
          sx={{
            "& .MuiListItemText-primary": {
              fontWeight: theme.typography.customFontWeight[1],
            },
          }}
          primary={primary}
        />
      )}
    </StyledListItemButton>
  </ListItem>
)

const ChipTag = ({
  chips = [],
  tags = [],
  autoClose = true,
  isChipsEdit = true,
  onChipsEditChange = () => { },
  onChipsRemoveChange = () => { },
  onModalDeleteClick = () => { },
  onChipsAddChange = () => { },
  onTagsChange = () => { }
}) => {
  const theme = useTheme();
  const [currentTag, setCurrentTag] = useState(structuredClone(tags));
  const [isHover, setIsHover] = useState(structuredClone(defaultHover));
  const [anchor, setAnchor] = useState(structuredClone(defaultAnchor));
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagValue, setEditingTagValue] = useState("");

  const [displayModal, setDisplayModal] = useState(false);
  const [displayModalTag, setDisplayModalTag] = useState(null);

  const handleCloseChip = () => {
    setAnchor(prev => ({ ...prev, open: null, delete: null, edit: null, add: null }));
    setSelectedChipIndex(null);
    setEditingTagId(null);
    setInputValue("")
  };

  const handleChipClick = (e, index) => {
    if (selectedChipIndex === index && !!anchor.delete) {
      handleCloseChip();
    } else {
      setAnchor(prev => ({ ...prev, open: e.currentTarget, edit: null }));
      setSelectedChipIndex(index);
    }
  };

  const handleChipEditClick = () => {
    setAnchor(prev => ({ ...prev, edit: prev.open, open: null }));
    const editedChip = chips[selectedChipIndex];
    setInputValue(editedChip.tagName);
  };

  const handleEdit = (e, mode) => {
    e.stopPropagation();
    if (e?.currentTarget) e.currentTarget.blur();

    const currentModeValue = mode === "chip" ? inputValue : editingTagValue

    const targetChip = mode === "chip"
      ? chips[selectedChipIndex]
      : currentTag.find((item) => item.tagId === editingTagId);

    const editedChip = {
      ...targetChip,
      tagName: currentModeValue,
    };

    const updatedChips = chips.map(chip =>
      chip.tagId === targetChip.tagId ? editedChip : chip
    );

    const updatedTags = currentTag.map(tag =>
      tag.tagId === targetChip.tagId
        ? { ...tag, tagName: currentModeValue }
        : tag
    );
    const cleanedTags = updatedTags.map(({ tagId, tagName }) => ({
      tagId,
      tagName
    }));

    onChipsEditChange({
      editedChip,
      updatedChips,
      updatedTags: cleanedTags
    });

    setInputValue("")
    setEditingTagId(null)
    setEditingTagValue(null)
    handleCloseChip();
  };

  const handleChipDelete = e => {
    if (e && e.currentTarget) {
      setAnchor(prev => ({ ...prev, delete: e.currentTarget }));
      e.currentTarget.blur()
    };

    const removedChip = chips[selectedChipIndex];
    const removedChips = chips.filter((chip, index) => index !== selectedChipIndex);

    onChipsRemoveChange({
      removedChip,
      removedChips
    });

    handleCloseChip();
  };

  const handleCloseChipEdit = () => {
    setAnchor(prev => ({ ...prev, edit: null }))
    setInputValue("");
  };
  const handleAddToChips = tag => {
    onChipsAddChange({
      newChip: { ...tag },
      newChips: [...chips, tag]
    });

    if (autoClose) setAnchor(prev => ({ ...prev, add: null }));
  };

  const handleAddTag = tag => {
    const trimmedName = tag?.trim();
    const isDuplicate = currentTag.some(loopTag => loopTag.tagName === trimmedName);
    if (!trimmedName || isDuplicate) return;

    const newExternalTag = { tagId: tag.tagId, tagName: trimmedName };
    const newExternalTags = [...currentTag, newExternalTag].map(loopTag => ({ tagId: loopTag.tagId, tagName: loopTag.tagName }));

    onTagsChange({
      newTag: newExternalTag,
      newTags: newExternalTags
    });

    const newInternalTag = { ...defaultTag, tagName: trimmedName };
    const newInternalTags = [...currentTag, newInternalTag].sort((a, b) => a.tagName.localeCompare(b.tagName));
    setCurrentTag(newInternalTags);
    setInputValue("");
  };

  const handleModalOpen = (e, currentTag) => {
    e.stopPropagation()
    setDisplayModal(true)
    setAnchor(prev => ({ ...prev, add: null }));
    setDisplayModalTag(currentTag)
  }

  const handleModalDelete = () => {
    const updatedChips = chips.filter((item) => item.tagId !== displayModalTag.tagId);

    const updatedTags = currentTag.filter((item) => item.tagId !== displayModalTag.tagId);
    const cleanedTags = updatedTags.map(({ tagId, tagName }) => ({
      tagId,
      tagName
    }));

    onModalDeleteClick({
      deleteChip: displayModalTag,
      updatedChips,
      updatedTags: cleanedTags
    })
    setDisplayModalTag(null)
    setDisplayModal(false)
  }

  useEffect(() => {
    // Filter the duplicated chips.
    const chipEnum = chips.reduce((acc, chip) => {
      return {
        ...acc,
        [chip.tagId]: chip
      }
    }, {});

    const newFilteredInternalTags = tags.map(tag => {
      return {
        ...tag,
        isShow: !chipEnum[tag.tagId]
      }
    }).sort((a, b) => a.tagName.localeCompare(b.tagName));

    setCurrentTag(structuredClone(newFilteredInternalTags));
  }, [chips, tags]);

  const usedTags = currentTag.filter(tag => tag.isShow === false);
  const unusedTags = currentTag
    .filter(tag => tag.isShow === true)
    .sort((a, b) => {
      if (a.tagName === "Root Node") return -1;
      if (b.tagName === "Root Node") return 1;
      return 0;
    });

  return (
    <>
      <ClickAwayListener onClickAway={handleCloseChip}>
        <StyledMainContainer>
          {chips.map((chip, index) => (
            <StyledChip
              key={`chip-${chip.tagId}-${index}`}
              label={chip.tagName}
              onClick={e => handleChipClick(e, index)}
            />
          ))}

          <IconButton
            onClick={e => setAnchor(prev => ({ ...prev, add: e.currentTarget, open: null }))}
            onMouseEnter={() => setIsHover(prev => ({ ...prev, add: true }))}
            onMouseLeave={() => setIsHover(prev => ({ ...prev, add: false }))}
            sx={{ p: 0 }}
          >
            {isHover?.add ? <AddIconHover /> : <AddIcon />}
          </IconButton>

          <Popover
            open={!!anchor?.open}
            anchorEl={anchor?.open}
            onClose={handleCloseChip}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disableRestoreFocus
            disableScrollLock
            slotProps={{
              root: {
                sx: {
                  pointerEvents: "none"
                }
              },
              paper: {
                sx: {
                  pointerEvents: "auto",
                  mt: 1, p: 1, boxShadow: 3, borderRadius: 2
                }
              }
            }}
          >
            <>
              <StyledRemoveButton
                sx={{
                  color: theme.palette.primary.redPrimary,
                  "&:hover": { color: theme.palette.primary.redSecound }
                }}
                onMouseEnter={() => setIsHover(prev => ({ ...prev, delete: true }))}
                onMouseLeave={() => setIsHover(prev => ({ ...prev, delete: false }))}
                startIcon={isHover?.delete ? <RemoveIconHover /> : <RemoveIcon />}
                color="inherit"
                onClick={handleChipDelete}
              >
                Remove from device
              </StyledRemoveButton>
              {
                isChipsEdit &&
                selectedChipIndex !== null &&
                chips[selectedChipIndex]?.tagName !== "Root Node" && (
                  <>
                    <Divider />
                    <StyledRemoveButton
                      onMouseEnter={() => setIsHover(prev => ({ ...prev, edit: true }))}
                      onMouseLeave={() => setIsHover(prev => ({ ...prev, edit: false }))}
                      startIcon={isHover?.edit ? <PencilIconHover /> : <PencilIcon />}
                      color="inherit"
                      onClick={handleChipEditClick}
                    >
                      Edit tag name
                    </StyledRemoveButton>
                  </>
                )
              }
            </>
          </Popover>

          <Popover
            open={!!anchor?.edit}
            anchorEl={anchor?.edit}
            onClose={handleCloseChip}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disableRestoreFocus
            disableScrollLock
            slotProps={{ paper: { sx: { width: 250, p: 2, borderRadius: 3 } } }}
          >
            <TextField
              fullWidth
              autoFocus
              size="small"
              variant="standard"
              value={inputValue}
              sx={{
                "& .MuiInputBase-input": {
                  color: theme.palette.primary.greyPrimary,
                },
              }}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      onClick={(e) => handleEdit(e, "chip")}
                      onMouseEnter={() => setIsHover(prev => ({ ...prev, inputEddit: true }))}
                      onMouseLeave={() => setIsHover(prev => ({ ...prev, inputEddit: false }))}
                      sx={{ p: 0, mb: "4px" }}
                      disabled={!inputValue}
                    >
                      {isHover?.inputEddit ? <AllowIconHover /> : <AllowIcon />}
                    </IconButton>
                    <IconButton
                      onClick={handleCloseChipEdit}
                      onMouseEnter={() => setIsHover(prev => ({ ...prev, cancel: true }))}
                      onMouseLeave={() => setIsHover(prev => ({ ...prev, cancel: false }))}
                      sx={{ p: 0, mb: "4px" }}
                    >
                      {isHover?.cancel ? <ClearIconHover /> : <ClearIcon />}
                    </IconButton>
                  </Box>
                ),
              }}
              onChange={e => setInputValue(e.target.value)}
            />
          </Popover>

          <Popover
            open={!!anchor?.add}
            anchorEl={anchor?.add}
            disableScrollLock
            onClose={handleCloseChip}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 250, p: 2, borderRadius: 3 } } }}
          >
            <TextField
              fullWidth
              size="small"
              variant="standard"
              value={inputValue}
              sx={{
                mb: 1.5,
                "& .MuiInputBase-input": {
                  color: theme.palette.primary.greyPrimary,
                },
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => handleAddTag(inputValue)}
                    onMouseEnter={() => setIsHover(prev => ({ ...prev, inputAdd: true }))}
                    onMouseLeave={() => setIsHover(prev => ({ ...prev, inputAdd: false }))}
                    sx={{ p: 0, mb: "4px" }}
                  >
                    {isHover?.inputAdd ? <AddIconHover /> : <AddIcon />}
                  </IconButton>
                ),
              }}
              onChange={e => setInputValue(e.target.value)}
            />

            <StyledScrollList>
              {
                usedTags.length > 0 && <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[2],
                    color: theme.palette.primary.greyFourth
                  }}
                >
                  Added
                </Typography>
              }
              <>
                {usedTags.map((tag, index) => (
                  <TagsList
                    theme={theme}
                    key={`used-${tag.tagId}-${index}`}
                    tag={tag}
                    isHover={isHover}
                    setIsHover={setIsHover}
                    handleEdit={handleEdit}
                    editingTagValue={editingTagValue}
                    setEditingTagId={setEditingTagId}
                    handleModalOpen={handleModalOpen}
                    isChipsEdit={isChipsEdit}
                    isEditing={editingTagId === tag.tagId}
                    setEditingTagValue={setEditingTagValue}
                    primary={tag.tagName}
                  />
                ))}
                {usedTags.length > 0 && unusedTags.length > 0 && <Divider />}
                {unusedTags.map((tag, index) => (
                  <TagsList
                    theme={theme}
                    key={`unused-${tag.tagId}-${index}`}
                    tag={tag}
                    isHover={isHover}
                    setIsHover={setIsHover}
                    handleEdit={handleEdit}
                    editingTagValue={editingTagValue}
                    setEditingTagId={setEditingTagId}
                    handleModalOpen={handleModalOpen}
                    isChipsEdit={isChipsEdit}
                    isEditing={editingTagId === tag.tagId}
                    setEditingTagValue={setEditingTagValue}
                    tagOnClick={handleAddToChips}
                    primary={tag.tagName}
                  />
                ))}
              </>
            </StyledScrollList>
          </Popover>
        </StyledMainContainer>
      </ClickAwayListener>

      {
        displayModal && <ModalContainer display={displayModal} modalWidth={"500px"}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              Delete Tag "{displayModalTag.tagName}"
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "25px 0",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                textAlign: "center",
                lineHeight: 1.4,
              }}
            >
              This will affect all devices currently using this tag. <br />
              Are you sure you want to proceed?
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "10px 0",
            }}
          >
            <Box margin="0 10px">
              <OutlineButton
                width="100px"
                height="32px"
                onClick={() => setDisplayModal(false)}
              >
                Cancel
              </OutlineButton>
            </Box>
            <Box margin="0 10px">
              <SolidWarnButton
                width="100px"
                height="32px"
                onClick={handleModalDelete}
              >
                Delete
              </SolidWarnButton>
            </Box>
          </Box>
        </ModalContainer>
      }
    </>
  );
};

export default ChipTag;